"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeHandshakeXeroInvoiceLineAmounts = computeHandshakeXeroInvoiceLineAmounts;
/** Round to 2 decimal places (money). */
function roundMoney(n) {
    return Math.round(n * 100) / 100;
}
const PAYIN_BASE_MATCH_EPS = 0.02;
/**
 * Splits a paid Handshake project invoice into Xero ACCREC line amounts.
 *
 * Handshake model (aligned with client payment UI and release logic):
 *
 * - **Client pays (invoice total):** milestone total + client’s share of the Handshake fee
 *   (`milestoneAmountCaptured + handshakeFeeAmountByClientCaptured`).
 * - **Business revenue (net):** milestone total − business’s share of the Handshake fee
 *   (`milestoneAmountCaptured − handshakeFeeAmountByBusinessCaptured`), per `payableByBusiness` on the project.
 * - **Handshake fee line:** when the client pays a fee share, a separate **client fee** line; when the
 *   business pays 100% of the fee, a **business fee** line on net revenue (see Xero adapter).
 *
 * Line items always satisfy: `businessRevenueAmount + totalHandshakeFeeAmount === clientPaidBaseAmount`.
 * Xero ACCREC line presentation may use gross milestone + client fee when the client shares the fee.
 *
 * @example 50% fee split — milestone $1,000, 5% fee ($50 → $25 business / $25 client)
 * - Client pays: $1,025
 * - Revenue line: $975 | Fee line: $50
 *
 * @example 100% business pays fee — milestone $500, 2% fee ($10 all business)
 * - Client pays: $500
 * - Revenue line: $490 | Fee line: $10
 */
function computeHandshakeXeroInvoiceLineAmounts(params) {
    const milestoneAmountCaptured = roundMoney(params.milestoneAmountCaptured);
    const handshakeFeeAmountByBusinessCaptured = roundMoney(params.handshakeFeeAmountByBusinessCaptured ?? 0);
    const handshakeFeeAmountByClientCaptured = roundMoney(params.handshakeFeeAmountByClientCaptured ?? 0);
    const totalHandshakeFeeAmount = roundMoney(handshakeFeeAmountByBusinessCaptured + handshakeFeeAmountByClientCaptured);
    const businessRevenueAmount = roundMoney(Math.max(0, milestoneAmountCaptured - handshakeFeeAmountByBusinessCaptured));
    const clientPaidBaseAmount = roundMoney(milestoneAmountCaptured + handshakeFeeAmountByClientCaptured);
    const paid = params.clientPaidAmount;
    let invoiceTotalAmount = clientPaidBaseAmount;
    if (paid != null && Number.isFinite(paid) && paid > 0) {
        if (Math.abs(paid - clientPaidBaseAmount) <= PAYIN_BASE_MATCH_EPS) {
            invoiceTotalAmount = roundMoney(paid);
        }
    }
    const paymentSurchargeAmount = paid != null && Number.isFinite(paid) && paid > clientPaidBaseAmount + PAYIN_BASE_MATCH_EPS
        ? roundMoney(paid - clientPaidBaseAmount)
        : 0;
    return {
        milestoneAmountCaptured,
        handshakeFeeAmountByBusinessCaptured,
        handshakeFeeAmountByClientCaptured,
        businessRevenueAmount,
        totalHandshakeFeeAmount,
        clientPaidBaseAmount,
        invoiceTotalAmount,
        paymentSurchargeAmount,
    };
}
